import * as React from 'react'
import {
  IonPage,
  IonContent,
  IonHeader,
  IonButton,
  IonItem,
  IonTextarea,
  IonNote,
  IonLoading,
  useIonRouter,
} from '@ionic/react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { format } from 'date-fns'

import { useAccount } from '../../contexts/authContext'
import { useToast } from '../../contexts/toastContext'
import Header from '../../components/header'
import { Card, CardContent } from '../../components/ui/card'
import { UnlayerTemplateSelect } from '../../integrations/unlayer/components/unlayer-template-select'
import { unlayerTemplates } from '../../integrations/unlayer/custom/unlayer-templates'
import { EmailProductSelect } from '../../integrations/unlayer/ai-email-builder/components/email-product-select'
import { Step } from '../../integrations/unlayer/ai-email-builder/components/step'
import { StepHeading } from '../../integrations/unlayer/ai-email-builder/components/step-heading'
import { UnlayerTemplateDisplay } from '../../integrations/unlayer/ai-email-builder/components/unlayer-template-display'
import { useCreateAIUnlayerDesign } from '../../integrations/unlayer/ai-email-builder/mutations'
import { useCreateAdminBroadcast } from '../../marketing/broadcasts/mutations'

type FormState = {
  selectedTemplate: number | null
  prompt: string
  selectedProducts: Array<string>
}

function BroadcastEmailBuilder() {
  const promptSectionRef = React.useRef<HTMLDivElement>(null)

  const { account } = useAccount()
  const canCreate = account.logo && account.websiteURL

  const { mutate: createBroadcast, isLoading: isCreatingBroadcast } =
    useCreateAdminBroadcast()
  const router = useIonRouter()
  const setToast = useToast()

  const { mutate: createAIUnlayerDesign, isLoading } =
    useCreateAIUnlayerDesign()

  const { control, handleSubmit, setValue, reset } = useForm<FormState>({
    defaultValues: {
      selectedTemplate: null,
      prompt: '',
      selectedProducts: [],
    },
  })

  const watchSelectedTemplate = useWatch({ control, name: 'selectedTemplate' })
  const watchPrompt = useWatch({ control, name: 'prompt' })
  const watchSelectedProducts = useWatch({ control, name: 'selectedProducts' })

  const template = unlayerTemplates.find((t) => t.id === watchSelectedTemplate)

  function handleCreateEmail(data: FormState) {
    if (!data.selectedTemplate)
      return setToast({
        message: 'An email template is required. Please select one.',
        color: 'danger',
      })
    createAIUnlayerDesign(
      {
        templateId: `${data.selectedTemplate}`,
        productIds: data.selectedProducts,
        prompt: data.prompt,
      },
      {
        onSuccess: (data) => {
          createBroadcast(
            {
              title: `Broadcast ${format(new Date(), 'MM/dd/yy')}`,
              unlayerDesign: data.unlayerDesign,
            },
            {
              onSuccess: (data) => {
                reset()
                router.push(
                  `/admin/campaigns/broadcast/${data.objectId}/html-email`,
                  'forward',
                  'replace'
                )
              },
            }
          )
        },
      }
    )
  }

  function handleSelectTemplate(id: number) {
    setValue('selectedTemplate', id)

    // Add a small delay to ensure the prompt section is rendered
    setTimeout(() => {
      promptSectionRef.current?.scrollIntoView({
        behavior: 'smooth',
      })
    }, 1000)
  }

  //
  const topRef = React.useRef<HTMLIonContentElement>(null)
  function resetForm() {
    topRef.current?.scrollToTop(500)
    reset()
  }

  return (
    <IonPage className="ion-background-dark">
      <IonHeader>
        <Header showMenu title="Magic Email Creator ✨" btn={null} />
      </IonHeader>
      {/* Fixed Action Buttons Container */}
      <div
        className={`bg-ion-background-color sticky top-0 z-10 py-4 shadow-md ${
          !canCreate && 'pointer-events-none blur-md filter'
        }`}
      >
        <div className="mx-auto w-full max-w-7xl">
          <div className="flex justify-center gap-4">
            {template ? (
              <IonButton
                color="yellow"
                size="large"
                className="rounded-xl px-6"
                onClick={resetForm}
              >
                Start Over
              </IonButton>
            ) : null}
            <IonButton
              color="yellow"
              size="large"
              className="rounded-xl px-6"
              onClick={handleSubmit(handleCreateEmail)}
              disabled={!template || !watchPrompt}
            >
              Create My Email! 🚀
            </IonButton>
          </div>
        </div>
      </div>
      <IonContent ref={topRef} className="relative" scrollY={canCreate}>
        {!canCreate ? (
          <>
            <div className="bg-ion-background-color absolute inset-0 z-10 opacity-10" />
            <div className="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2">
              <div className="max-w-md shadow">
                <Card>
                  <CardContent>
                    <p className="text-xl">
                      You're account is not properly set up to create
                      AI-generated emails. Please contact your OneShop rep to
                      finish setting up your account.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </>
        ) : null}
        <div
          className={`ion-padding relative ${
            !canCreate && 'pointer-events-none blur-md filter'
          }`}
        >
          {/* Main Content Section */}
          <h2 className="text-ion-color-light py-2 text-center text-2xl font-semibold">
            Let's Create Your Amazing Email! 🎨
          </h2>

          <div className="space-y-4">
            {/* Step 1: Template Selection */}
            <Step isComplete={Boolean(template)} step="1">
              <StepHeading>Pick Your Email Design</StepHeading>
              <div className="flex flex-col items-center justify-center gap-y-2">
                <UnlayerTemplateSelect onClick={handleSelectTemplate} />

                {template ? (
                  <UnlayerTemplateDisplay template={template} />
                ) : null}
              </div>
            </Step>

            {/* Step 2: AI Prompt */}
            <div ref={promptSectionRef} />
            <Step isComplete={Boolean(watchPrompt)} step="2">
              <StepHeading>Tell AI What to Write</StepHeading>
              <IonItem color="secondary" className="rounded-lg" lines="none">
                <Controller
                  name="prompt"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <IonTextarea
                      value={value}
                      onIonChange={(e) => {
                        onChange(e.detail.value)
                      }}
                      placeholder="Example: Write a fun email about our summer collection with bright colors and beach themes!"
                      className="ion-margin-top"
                      rows={3}
                      autoGrow={true}
                    />
                  )}
                />
              </IonItem>
              <IonNote color="medium" className="ion-padding-top">
                💡 Tip: The more details you give, the better your email will
                be!
              </IonNote>
            </Step>

            {/* Step 3: Product Selection */}
            <Step isComplete={watchSelectedProducts.length > 0} step="3">
              <StepHeading>Choose Your Products (optional)</StepHeading>
              <Controller
                name="selectedProducts"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <EmailProductSelect onSelect={onChange} />
                )}
              />
            </Step>
          </div>
        </div>

        <IonLoading
          isOpen={isLoading || isCreatingBroadcast}
          message={
            isLoading
              ? 'Generating your email...'
              : isCreatingBroadcast
              ? 'Creating your broadcast...'
              : 'Loading...'
          }
        />
      </IonContent>
    </IonPage>
  )
}

export { BroadcastEmailBuilder }
